import StarIcon from '@mui/icons-material/Star';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import PropTypes from 'prop-types';

// Explains what checking the box will actually do to the product, so silently
// displacing an existing default is never a surprise (POM-5707).
const getHintText = (productId, currentDefault, lookupDone) => {
  if (!lookupDone) return `Checking the current default for ${productId}…`;
  if (currentDefault) {
    const label = [currentDefault.drugName, currentDefault.strength].filter(Boolean).join(' · ');
    return `Replaces the current default, ${currentDefault.ndc}${label ? ` (${label})` : ''}.`;
  }
  return `${productId} has no default NDC yet.`;
};

// POM-5707 — a brand new NDC has no facility stock by definition, so it cannot
// be promoted from the inventory grid's Edit Default NDC dialog until someone
// receives stock against it. Offering the choice at creation time closes that
// gap. The default NDC is a dispensing preference, not a stock gate.
const SetDefaultNdcToggle = ({ productId, currentDefault, lookupDone, checked, onChange }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: 1,
      border: '1px solid',
      borderColor: checked ? 'primary.main' : 'divider',
      bgcolor: checked ? 'action.selected' : 'transparent',
    }}
  >
    <FormControlLabel
      sx={{ m: 0, alignItems: 'flex-start' }}
      control={
        <Checkbox
          size='small'
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          sx={{ pt: 0.25 }}
        />
      }
      label={
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <StarIcon sx={{ fontSize: '0.9rem', color: '#92400E' }} />
            <Typography variant='body2' sx={{ fontWeight: 600 }}>
              Set as the default NDC to dispense
            </Typography>
          </Box>
          <Typography variant='caption' color='text.secondary'>
            {getHintText(productId, currentDefault, lookupDone)}
          </Typography>
        </Box>
      }
    />
  </Box>
);

SetDefaultNdcToggle.propTypes = {
  productId: PropTypes.string.isRequired,
  currentDefault: PropTypes.shape({
    ndc: PropTypes.string,
    drugName: PropTypes.string,
    strength: PropTypes.string,
  }),
  lookupDone: PropTypes.bool,
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};
SetDefaultNdcToggle.defaultProps = {
  currentDefault: null,
  lookupDone: false,
  checked: false,
};

export default SetDefaultNdcToggle;
